import { useState, useEffect, useCallback } from 'react'
import { useApp } from '../context/AppContext'
import pushSubscriptionService from '../services/pushSubscriptionService'

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY

/** Convierte la clave pública VAPID (base64url) a Uint8Array para pushManager. */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i)
  return output
}

/** ¿El navegador soporta Web Push? */
export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window

/**
 * Hook para gestionar las notificaciones Web Push de ESTE navegador:
 * pedir permiso, suscribir/desuscribir y persistir la suscripción.
 * @hook
 */
export default function usePushNotifications() {
  const { user } = useApp()
  const supported = isPushSupported()

  const [permission, setPermission] = useState(
    supported ? Notification.permission : 'unsupported'
  )
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Estado inicial: ¿ya hay una suscripción activa en este navegador?
  useEffect(() => {
    if (!supported) return
    let active = true
    ;(async () => {
      try {
        const reg = await navigator.serviceWorker.ready
        const sub = await reg.pushManager.getSubscription()
        if (active) setIsSubscribed(!!sub)
      } catch (err) {
        console.warn('No se pudo leer la suscripción push:', err)
      }
    })()
    return () => {
      active = false
    }
  }, [supported])

  const subscribe = useCallback(async () => {
    if (!supported) {
      setError('Este navegador no soporta notificaciones push.')
      return false
    }
    if (!VAPID_PUBLIC_KEY) {
      setError('Falta configurar VITE_VAPID_PUBLIC_KEY.')
      return false
    }
    setLoading(true)
    setError(null)
    try {
      const perm = await Notification.requestPermission()
      setPermission(perm)
      if (perm !== 'granted') {
        setError('Permiso de notificaciones denegado.')
        return false
      }

      const reg = await navigator.serviceWorker.ready
      let sub = await reg.pushManager.getSubscription()
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        })
      }

      const json = sub.toJSON()
      const res = await pushSubscriptionService.upsert({
        userId: user?.id ?? null,
        endpoint: sub.endpoint,
        p256dh: json.keys?.p256dh,
        auth: json.keys?.auth,
        userAgent: navigator.userAgent,
      })
      if (!res.success) throw new Error(res.error)

      setIsSubscribed(true)
      return true
    } catch (err) {
      console.error('Error al suscribir push:', err)
      setError(err.message || 'No se pudo activar las notificaciones.')
      return false
    } finally {
      setLoading(false)
    }
  }, [supported, user])

  const unsubscribe = useCallback(async () => {
    if (!supported) return
    setLoading(true)
    setError(null)
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) {
        await pushSubscriptionService.removeByEndpoint(sub.endpoint)
        await sub.unsubscribe()
      }
      setIsSubscribed(false)
    } catch (err) {
      console.error('Error al desuscribir push:', err)
      setError(err.message || 'No se pudo desactivar las notificaciones.')
    } finally {
      setLoading(false)
    }
  }, [supported])

  return {
    supported,
    permission,
    isSubscribed,
    loading,
    error,
    subscribe,
    unsubscribe,
  }
}
